import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  CompanyRoleDTO,
  ModulePermissionOverridesDTO,
  UpdateModulePermissionOverridePayload,
} from '@vyce/core/src/api/types';
import { StaffListItem } from '@vyce/core/src/types';
import { toggleId } from '@vyce/core/src/utils/staffPermissions';
import { RolesModuleContext } from '@vyce/core/src/contexts/rolesModuleProvider';
import { RolesEditorSaveOutcome } from '@vyce/core/src/contexts/rolesSaveBarProvider';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';

import { NotificationContext } from '../../../contexts/notificationContext';
import { ModuleDraft, ModulePermissionState, VisibleStaffModule } from '../types';
import {
  applyRoleReassignments,
  buildRolesPayload,
  buildVisibleModules,
  CompanyDraft,
  draftFromStaffItem,
  findStaffMember,
  getDraftInvalidReason,
  getRoleReassignments,
} from '../utils/roleDraft';
import {
  buildCatalogueIndex,
  CatalogueIndex,
  closure,
  emptyPermissionState,
  explicitFromEffective,
  moduleRoleDefaults,
  ownPermissions,
  permissionStateFromDTO,
  setsEqual,
  toDisplayName,
} from '../utils/permissionState';
import { getSaveErrorNotification, parsePermissionConflicts } from '../utils/permissionErrors';
import { usePermissionCatalogue } from './usePermissionCatalogue';
import { useCompanyRolesData } from './useCompanyRolesData';

type EditorStatus = 'idle' | 'loading' | 'ready' | 'readonly' | 'noPosition' | 'error';

type PermissionDraft = Record<string, ModulePermissionState>;

interface Props {
  companyId?: string;
  userId: string;
  /** Only needed when the caller has no staffItem and we must go looking. */
  userLastName?: string;
  /**
   * The staff row, when the caller already holds it (a staff list does). Skips
   * findStaffItem's 1–2 extra /staff round-trips per expanded row. Only four
   * fields are used: resources, position_name, owner, modules.
   */
  staffItem?: StaffListItem;
  enabled: boolean;
}

const draftsEqual = (a: ModuleDraft, b: ModuleDraft) =>
  (a.role?.role_id || null) === (b.role?.role_id || null) &&
  (a.unresolvedRoleName || '') === (b.unresolvedRoleName || '') &&
  [...a.resourceIds].sort().join(',') === [...b.resourceIds].sort().join(',');

export const useCompanyRolesEditor = ({
  companyId,
  userId,
  userLastName,
  staffItem: providedStaffItem,
  enabled,
}: Props) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const {
    canEdit,
    getStaffRequest,
    updateStaffPermissionsRequest,
    getUserPermissionOverridesRequest,
    updateUserPermissionOverridesRequest,
  } = useContext(RolesModuleContext);
  const { fetchCatalogue } = usePermissionCatalogue();
  const { fetchCompanyRolesData } = useCompanyRolesData();
  const { isInternalAdmin } = usePermissions();

  const [status, setStatus] = useState<EditorStatus>('idle');
  const [visibleModules, setVisibleModules] = useState<VisibleStaffModule[]>([]);
  const [draft, setDraft] = useState<CompanyDraft>({});
  const [baseline, setBaseline] = useState<CompanyDraft>({});
  const [permissions, setPermissions] = useState<PermissionDraft>({});
  const [permissionBaseline, setPermissionBaseline] = useState<PermissionDraft>({});
  const [catalogue, setCatalogue] = useState<CatalogueIndex | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [positionName, setPositionName] = useState('');
  const [saving, setSaving] = useState(false);
  const staffResourcesRef = useRef<Record<string, string[]>>({});
  const loadStartedRef = useRef(false);

  const resolveLabel = useCallback(
    (systemName: string) => (catalogue ? toDisplayName(systemName, catalogue.index) : systemName),
    [catalogue]
  );

  const findStaffItem = useCallback(async (): Promise<StaffListItem | undefined> => {
    if (!companyId) return undefined;
    return findStaffMember(getStaffRequest, companyId, userId, userLastName);
  }, [companyId, userId, userLastName, getStaffRequest]);

  // 404 means the person holds no position in this company — not an error.
  const loadOverrides = useCallback(
    async (id: string): Promise<ModulePermissionOverridesDTO[] | null> => {
      try {
        const res = await getUserPermissionOverridesRequest(id, userId);
        return res.data || [];
      } catch (e: any) {
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
    [getUserPermissionOverridesRequest, userId]
  );

  const load = useCallback(async () => {
    if (!companyId) {
      setStatus('readonly');
      return;
    }
    try {
      setStatus('loading');
      // roles + the resource inventory come from a company-level cache, so a list
      // of staff rows shares one answer instead of refetching per row.
      const [companyData, staffItem, catalogueModules, overrides] = await Promise.all([
        fetchCompanyRolesData(companyId),
        providedStaffItem ? Promise.resolve(providedStaffItem) : findStaffItem(),
        fetchCatalogue(companyId),
        loadOverrides(companyId),
      ]);
      const { roles: rolesData, resources: moduleResources, readable } = companyData;
      if (!staffItem) {
        setStatus('readonly');
        return;
      }
      staffResourcesRef.current = staffItem.resources || {};
      setPositionName((staffItem as any).position_name || '');
      setIsOwner(!!staffItem.owner);

      const catalogueIndex = buildCatalogueIndex(catalogueModules);
      setCatalogue(catalogueIndex);

      const overridesByModule = new Map((overrides || []).map(item => [item.module_system_name, item]));

      const modules = buildVisibleModules({
        catalogueModules,
        catalogueIndex,
        rolesData,
        moduleResources,
        readable,
        isInternalAdmin,
        keepWithoutRoles: systemName => overridesByModule.has(systemName),
      });

      const initialDraft = draftFromStaffItem(modules, staffItem);
      const initialPermissions: PermissionDraft = {};
      modules.forEach(module => {
        const dto = overridesByModule.get(module.systemName);
        initialPermissions[module.systemName] = dto
          ? permissionStateFromDTO(dto, catalogueIndex)
          : emptyPermissionState();
      });

      setVisibleModules(modules);
      setDraft(initialDraft);
      setBaseline(initialDraft);
      setPermissions(initialPermissions);
      setPermissionBaseline(initialPermissions);
      setStatus(overrides === null ? 'noPosition' : 'ready');
    } catch (e) {
      setStatus('error');
      handleServerError(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    findStaffItem,
    loadOverrides,
    fetchCatalogue,
    fetchCompanyRolesData,
    providedStaffItem,
    isInternalAdmin,
  ]);

  useEffect(() => {
    if (enabled && !loadStartedRef.current) {
      loadStartedRef.current = true;
      load();
    }
  }, [enabled, load]);

  const setModuleRole = useCallback(
    (moduleKey: string, role: CompanyRoleDTO | null) => {
      // Everything this click reassigns, worked out once so the role draft and the
      // permission layer below can't disagree about it.
      const reassigned = getRoleReassignments(draft, moduleKey, role, visibleModules);

      setDraft(prev => applyRoleReassignments(prev, reassigned, visibleModules, staffResourcesRef.current));

      // The checkboxes belong to the role: leaving them on the previous role's set
      // would show — and save — permissions the new role never grants.
      // permission_names are system_names, the keys the catalogue is indexed by.
      if (!catalogue) return;
      setPermissions(prev => {
        const next = { ...prev };
        reassigned.forEach((nextRole, systemName) => {
          const current = next[systemName] || emptyPermissionState();
          const module = visibleModules.find(item => item.systemName === systemName);

          // Back to the role the person actually holds: the saved state is exact —
          // permission_overrides gave us its role_defaults — so restore it instead
          // of re-deriving anything.
          const savedRoleId = baseline[systemName]?.role?.role_id || null;
          const savedState = permissionBaseline[systemName];
          if (nextRole && savedState && nextRole.role_id === savedRoleId) {
            next[systemName] = { ...savedState, conflicts: [] };
            return;
          }

          // No role means no access, which is unambiguous. For a role, read what it
          // grants; null there means the payload didn't tell us, and blanking the
          // list would be a guess that reads as "this role grants nothing".
          // Kept to this module's own names, as the server's are, so it compares
          // like for like with effectiveByModule.
          const defaults = nextRole ? moduleRoleDefaults(nextRole, module, catalogue) : [];
          if (defaults === null) {
            // eslint-disable-next-line no-console
            console.warn(
              `[roles] ${systemName}: role "${nextRole?.role_name}" carries no readable permission list — leaving the checkboxes as they are. Role payload:`,
              nextRole
            );
            return;
          }
          next[systemName] = {
            ...current,
            roleDefaults: defaults,
            explicit: Array.from(explicitFromEffective(defaults, catalogue.index)),
            // These described the diff against the role being replaced.
            added: [],
            removed: [],
            conflicts: [],
          };
        });
        return next;
      });
    },
    [visibleModules, catalogue, draft, baseline, permissionBaseline]
  );

  const toggleModuleResource = useCallback((moduleKey: string, resourceId: string, checked: boolean) => {
    setDraft(prev => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        resourceIds: toggleId(prev[moduleKey]?.resourceIds || [], resourceId, checked),
      },
    }));
  }, []);

  const toggleAllModuleResources = useCallback(
    (moduleKey: string, checked: boolean) => {
      const module = visibleModules.find(item => item.systemName === moduleKey);
      setDraft(prev => ({
        ...prev,
        [moduleKey]: {
          ...prev[moduleKey],
          resourceIds: checked ? (module?.resources || []).map(item => item.id) : [],
        },
      }));
    },
    [visibleModules]
  );

  // --- permission layer -----------------------------------------------------

  const togglePermission = useCallback((moduleKey: string, systemName: string, checked: boolean) => {
    setPermissions(prev => {
      const current = prev[moduleKey] || emptyPermissionState();
      return {
        ...prev,
        [moduleKey]: {
          ...current,
          explicit: toggleId(current.explicit, systemName, checked),
          conflicts: [],
        },
      };
    });
  }, []);

  // Ticks the role's own set, and saving then sends exactly that: the server sees
  // no difference from the defaults and drops the override. Clearing the boxes
  // instead would save as "remove everything". Same move as "Match module default"
  // for a per-resource exception.
  const resetModulePermissions = useCallback(
    (moduleKey: string) => {
      if (!catalogue) return;
      setPermissions(prev => {
        const current = prev[moduleKey] || emptyPermissionState();
        return {
          ...prev,
          [moduleKey]: {
            ...current,
            explicit: Array.from(explicitFromEffective(current.roleDefaults, catalogue.index)),
            conflicts: [],
          },
        };
      });
    },
    [catalogue]
  );

  const setClearResourceOverrides = useCallback((moduleKey: string, value: boolean) => {
    setPermissions(prev => {
      const current = prev[moduleKey] || emptyPermissionState();
      return { ...prev, [moduleKey]: { ...current, clearResourceOverrides: value } };
    });
  }, []);

  // Scoped to the module's own permissions. implied_permissions cross module
  // boundaries (a Time permission can imply one under Company), so the raw closure
  // holds names from other modules — while the server's `effective` and
  // `role_defaults` are per module. Compared unscoped, any module with such an
  // implication read as dirty from the moment it loaded, re-sent its overrides on
  // every save, and never went clean.
  const effectiveByModule = useMemo(() => {
    const result: Record<string, Set<string>> = {};
    if (!catalogue) return result;
    visibleModules.forEach(module => {
      const state = permissions[module.systemName];
      result[module.systemName] = ownPermissions(module, closure(state?.explicit || [], catalogue.index));
    });
    return result;
  }, [permissions, visibleModules, catalogue]);

  const dirtyPermissionModules = useMemo(
    () =>
      visibleModules
        .filter(module => {
          const state = permissions[module.systemName];
          const base = permissionBaseline[module.systemName];
          if (!state || !base) return false;
          const effective = effectiveByModule[module.systemName] || new Set<string>();
          // Put the reference set through the same closure-and-scope as `effective`,
          // so a server set that isn't closed, or carries another module's names,
          // can't make an untouched module read as edited.
          const normalise = (names: string[]) =>
            catalogue ? ownPermissions(module, closure(names, catalogue.index)) : new Set(names);
          const current = draft[module.systemName];
          const baseDraft = baseline[module.systemName];
          const roleChanged = !!current && !!baseDraft && !draftsEqual(current, baseDraft);
          // Saving the role already resets this module to the new role's defaults,
          // so compare against those: otherwise every role change would also PUT
          // an overrides row that merely restates them.
          if (roleChanged) {
            return !setsEqual(effective, normalise(state.roleDefaults));
          }
          return !setsEqual(effective, normalise(base.baselineEffective));
        })
        .map(module => module.systemName),
    [visibleModules, permissions, permissionBaseline, effectiveByModule, draft, baseline, catalogue]
  );

  const discard = useCallback(() => {
    setDraft(baseline);
    setPermissions(permissionBaseline);
  }, [baseline, permissionBaseline]);

  const rolesDirty = useMemo(
    () =>
      visibleModules.some(module => {
        const current = draft[module.systemName];
        const base = baseline[module.systemName];
        if (!current || !base) return false;
        return !draftsEqual(current, base);
      }),
    [draft, baseline, visibleModules]
  );

  const isDirty = rolesDirty || dirtyPermissionModules.length > 0;

  const invalidReason = useMemo(() => getDraftInvalidReason(draft, visibleModules), [draft, visibleModules]);

  const save = useCallback(
    async (opts?: { silent?: boolean }): Promise<RolesEditorSaveOutcome> => {
      if (!companyId || invalidReason || isOwner || !isDirty) return { status: 'skipped' };
      // Roles saved against a module the company has nothing in yet. Named here so a
      // rejection can say which module, rather than echoing a schema error. Cleared
      // once that request is through, so a later failure isn't blamed on the scope.
      let emptyScopeLabels: string[] = [];
      // Worked out before the baseline moves: these are the modules whose permission
      // layer has to be rebased once the role write lands.
      const roleChangedModules = visibleModules
        .filter(module => {
          const current = draft[module.systemName];
          const base = baseline[module.systemName];
          return !!current && !!base && !draftsEqual(current, base);
        })
        .map(module => module.systemName);
      try {
        setSaving(true);

        if (rolesDirty) {
          const payload = buildRolesPayload(draft, visibleModules, companyId);
          const { roles } = payload;
          emptyScopeLabels = payload.emptyScopeLabels;

          await updateStaffPermissionsRequest(companyId, userId, { position_name: positionName, roles });
          emptyScopeLabels = [];
        }

        let nextPermissions = permissions;

        // Saving a role replaces that module's server-side defaults, so the
        // permission layer has to be rebased against the new ones — and not only
        // when a permission was edited too. Without this, a plain role change
        // leaves baselineEffective describing the *previous* role, the module reads
        // as dirty on every later comparison, and the Save bar never goes away.
        if (rolesDirty && catalogue) {
          const rebased = await loadOverrides(companyId);
          const byModule = new Map((rebased || []).map(item => [item.module_system_name, item]));
          nextPermissions = { ...permissions };
          roleChangedModules.forEach(systemName => {
            const dto = byModule.get(systemName);
            const current = nextPermissions[systemName] || emptyPermissionState();
            if (!dto) {
              // No overrides row for a module we just re-roled: the ticks on screen
              // are the role's own set, so make them the baseline locally.
              const effective = Array.from(effectiveByModule[systemName] || []);
              nextPermissions[systemName] = {
                ...current,
                roleDefaults: effective,
                baselineEffective: effective,
              };
              return;
            }
            // A module whose permissions were edited too keeps its ticks — the PUT
            // below sends them — and takes only the defaults they're diffed against.
            nextPermissions[systemName] = dirtyPermissionModules.includes(systemName)
              ? { ...current, roleDefaults: dto.role_defaults || [] }
              : permissionStateFromDTO(dto, catalogue);
          });
        }

        if (dirtyPermissionModules.length) {
          const payload: UpdateModulePermissionOverridePayload[] = dirtyPermissionModules.map(
            systemName => {
              const module = visibleModules.find(item => item.systemName === systemName);
              const effective = effectiveByModule[systemName] || new Set<string>();
              return {
                module_system_name: systemName,
                // Always the full ticked state, in catalogue order — never [] as a
                // shorthand for "reset". The server diffs what arrives against the role
                // defaults, so an empty list reads as "remove every one of them", which
                // is how admins ended up with a role and no permissions.
                permissions: (module?.permissions || [])
                  .map(permission => permission.system_name)
                  .filter(name => effective.has(name)),
                clear_resource_overrides: nextPermissions[systemName]?.clearResourceOverrides ?? true,
              };
            }
          );

          const res = await updateUserPermissionOverridesRequest(companyId, userId, payload);
          if (catalogue) {
            const byModule = new Map((res.data || []).map(item => [item.module_system_name, item]));
            const merged = { ...nextPermissions };
            visibleModules.forEach(module => {
              const dto = byModule.get(module.systemName);
              if (dto) {
                merged[module.systemName] = permissionStateFromDTO(dto, catalogue);
                return;
              }
              // The response left out a module we sent: its ticks matched the role
              // defaults, so the server dropped the override row. Record what we sent
              // as the new baseline. Modules we didn't send are left alone — the
              // rebase above already put them in step with the server.
              if (!dirtyPermissionModules.includes(module.systemName)) return;
              const effective = Array.from(effectiveByModule[module.systemName] || []);
              merged[module.systemName] = {
                ...(merged[module.systemName] || emptyPermissionState()),
                baselineEffective: effective,
                conflicts: [],
              };
            });
            nextPermissions = merged;
          }
        }

        setPermissions(nextPermissions);
        setPermissionBaseline(nextPermissions);
        setBaseline(draft);
        if (!opts?.silent) {
          showNotification({ message: 'Permissions have been updated', options: { variant: 'success' } });
        }
        return { status: 'saved' };
      } catch (e: any) {
        const notification = getSaveErrorNotification(e, resolveLabel, emptyScopeLabels);
        // The conflict badges are UI state, not a message — paint them either way,
        // so a caller that silenced the snackbar still shows what went wrong.
        if (e?.response?.status === 409) {
          const conflicts = parsePermissionConflicts(e);
          if (conflicts.length) {
            setPermissions(prev => {
              const next = { ...prev };
              dirtyPermissionModules.forEach(systemName => {
                next[systemName] = { ...(next[systemName] || emptyPermissionState()), conflicts };
              });
              return next;
            });
          }
        }
        if (!opts?.silent) {
          if (notification) {
            showNotification(notification);
          } else {
            handleServerError(e);
          }
        }
        return { status: 'failed', error: e };
      } finally {
        setSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      companyId,
      userId,
      positionName,
      draft,
      baseline,
      visibleModules,
      invalidReason,
      isOwner,
      isDirty,
      rolesDirty,
      dirtyPermissionModules,
      effectiveByModule,
      permissions,
      catalogue,
      resolveLabel,
    ]
  );

  return {
    status,
    canEdit: canEdit && !isOwner,
    isOwner,
    visibleModules,
    draft,
    baseline,
    permissions,
    permissionBaseline,
    effectiveByModule,
    catalogue,
    saving,
    isDirty,
    invalidReason,
    setModuleRole,
    toggleModuleResource,
    toggleAllModuleResources,
    togglePermission,
    resetModulePermissions,
    setClearResourceOverrides,
    discard,
    save,
    retry: load,
  };
};
